import {PackageLabel,ServiceIdentity} from "../../ui/ServiceIdentity";
import { CustomerError } from "../../ui/CustomerError";
import { BlobCtaAction, BlobCtaButton, CardGlass, GlassChip, GlassIconButton, Typography } from "@cupcodev/ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CtaLink, EmptyState, Facts, FilterTabs, NaveIcon, Notice, PageHeader, PageSkeleton, Panel, RowIcon, RowLink, RowList, SectionSkeleton } from "../../ui/NavePage";
import { isCustomerDependencyErrorCode } from "../../application/foundation/customer-read-port-error";
import type { CustomerBillingSummary } from "../../read-models/billing/customer-billing-summary";
import type {
  CustomerInvoiceDetail,
  CustomerInvoiceList,
} from "../../read-models/billing/customer-invoice-read-model";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";

type QueryError = Readonly<{ code: string }>;
type QueryResult<T> = Readonly<{ ok: true; value: T }> | Readonly<{ ok: false; error: QueryError }>;
type LoadState<T> =
  | Readonly<{ kind: "loading" }>
  | Readonly<{ kind: "ready"; value: T }>
  | Readonly<{ kind: "error"; code: string }>;

const useLoad = <T,>(loadKey: string, load: () => Promise<QueryResult<T>>): LoadState<T> => {
  const [state, setState] = useState<LoadState<T>>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void load()
      .then((result) => {
        if (cancelled) return;
        setState(result.ok
          ? { kind: "ready", value: result.value }
          : { kind: "error", code: result.error.code });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error", code: "UNEXPECTED" });
      });
    return () => { cancelled = true; };
  }, [loadKey]);

  return state;
};

const ErrorState = ({ code, inline = false }: { code: string; inline?: boolean }) => {
  const { t } = useI18n();
  const dependency = isCustomerDependencyErrorCode(code);
  const forbidden = code === "FORBIDDEN";
  const notFound = code === "NOT_FOUND";
  const titleKey = dependency
    ? "billing.errors.unavailable.title"
    : forbidden
      ? "billing.errors.forbidden.title"
      : notFound
        ? "billing.errors.notFound.title"
        : "billing.errors.generic.title";
  const bodyKey = dependency
    ? "billing.errors.unavailable.body"
    : forbidden
      ? "billing.errors.forbidden.body"
      : notFound
        ? "billing.errors.notFound.body"
        : "billing.errors.generic.body";
  return <CustomerError inline={inline} title={t(titleKey)} description={t(bodyKey)} />;
};

const invoiceStatusKey = (status: string): string => `billing.invoice.status.${status.toLowerCase()}`;
const paymentStatusKey = (status: string): string => `billing.payment.status.${status.toLowerCase()}`;

const statusVariant = (status: string): "success" | "neutral" | "destructive" =>
  status === "OVERDUE" || status === "FAILED" ? "destructive" : status === "PAID" ? "success" : "neutral";

const BillingInvoiceListSection = ({
  initial,
  loadKey,
  loadNext,
}: {
  initial: CustomerInvoiceList;
  loadKey: string;
  loadNext(cursor: string): Promise<QueryResult<CustomerInvoiceList>>;
}) => {
  const { t, formatCurrency, formatDate, formatMonth } = useI18n();
  const [items, setItems] = useState(initial.items);
  const [nextCursor, setNextCursor] = useState<string | undefined>(initial.nextCursor);
  const [hasMore, setHasMore] = useState(initial.hasMore);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<InvoiceFilter>("all");

  useEffect(() => {
    setItems(initial.items);
    setNextCursor(initial.nextCursor);
    setHasMore(initial.hasMore);
    setLoadingMore(false);
    setLoadMoreError(undefined);
  }, [loadKey, initial]);

  const loadMore = async (): Promise<void> => {
    if (loadingMore || nextCursor === undefined) return;
    setLoadingMore(true);
    setLoadMoreError(undefined);
    try {
      const result = await loadNext(nextCursor);
      if (!result.ok) {
        setLoadMoreError(result.error.code);
        return;
      }
      const byId = new Map(items.map((item) => [item.invoiceId, item]));
      for (const item of result.value.items) byId.set(item.invoiceId, item);
      setItems([...byId.values()]);
      setNextCursor(result.value.nextCursor);
      setHasMore(result.value.hasMore);
    } catch {
      setLoadMoreError("UNEXPECTED");
    } finally {
      setLoadingMore(false);
    }
  };

  const counts = {
    all: items.length,
    open: items.filter((item) => item.status === "OPEN").length,
    overdue: items.filter((item) => item.status === "OVERDUE").length,
    paid: items.filter((item) => item.status === "PAID").length,
  };
  const visible = filter === "all" ? items : items.filter((item) => item.status === filter.toUpperCase());

  if (items.length === 0) return <EmptyState icon="lucide:receipt-text">{t("billing.invoices.empty")}</EmptyState>;

  return (
    <>
      <div className="nave-toolbar">
        <FilterTabs<InvoiceFilter> label={t("layout.filter")} value={filter} onChange={setFilter} options={[
          { value: "all", label: t("layout.filter.all"), count: counts.all },
          { value: "open", label: t("billing.invoice.status.open"), count: counts.open },
          { value: "overdue", label: t("billing.invoice.status.overdue"), count: counts.overdue },
          { value: "paid", label: t("billing.invoice.status.paid"), count: counts.paid },
        ]} />
      </div>
      {visible.length === 0 ? <EmptyState icon="lucide:filter-x">{t("layout.noResults")}</EmptyState> : (
        <RowList label={t("billing.invoices.title")}>
          {visible.map((invoice) => (
            <li key={invoice.invoiceId} className="nave-row-with-action">
              <RowLink
                href={`/billing/invoices/${invoice.invoiceId}`}
                leading={invoice.serviceLogoUrl ? <img src={invoice.serviceLogoUrl} alt="" /> : <RowIcon name="lucide:receipt-text" tone={invoice.status === "OVERDUE" ? "alert" : invoice.status === "PAID" ? "success" : "neutral"} />}
                title={<>
                  <PackageLabel text={invoice.serviceName || invoice.planName || t("billing.invoice.label")}/>
                  {invoice.billingPhase ? <GlassChip variant="purple">{t(`billing.phase.${invoice.billingPhase}`)}</GlassChip> : null}
                </>}
                subtitle={invoice.invoiceNumber ? t("billing.invoice.detailTitle", { id: invoice.invoiceNumber }) : undefined}
                meta={<>
                  {invoice.competence ? <span className="nave-meta-item"><NaveIcon name="lucide:calendar-range" />{formatMonth(invoice.competence)}</span> : null}
                  <span className="nave-meta-item"><NaveIcon name="lucide:calendar-clock" />{t("billing.due")}: {formatDate(invoice.dueAt)}</span>
                  {invoice.paidAt ? <span className="nave-meta-item"><NaveIcon name="lucide:circle-check" />{t("billing.paidAt")}: {formatDate(invoice.paidAt)}</span> : invoice.issuedAt ? <span className="nave-meta-item"><NaveIcon name="lucide:calendar" />{t("billing.issuedAt")}: {formatDate(invoice.issuedAt)}</span> : null}
                </>}
                trailing={<>
                  <span className="nave-row-amount">{formatCurrency(invoice.amount.amount, invoice.amount.currency)}</span>
                  <GlassChip variant={statusVariant(invoice.status)}>{t(invoiceStatusKey(invoice.status))}</GlassChip>
                </>}
              />
              <GlassIconButton type="button" variant="purple" size="sm" onClick={() => window.location.assign(`/api/invoices/${encodeURIComponent(invoice.invoiceId)}/download`)} aria-label={`${t("billing.invoice.download")} · ${invoice.serviceName || invoice.invoiceNumber || t("billing.invoice.label")}`} title={t("billing.invoice.download")}>
                <NaveIcon name="lucide:download" />
              </GlassIconButton>
            </li>
          ))}
        </RowList>
      )}
      {counts.paid > 0 && (filter === "all" || filter === "paid") ? <Notice icon="lucide:file-check">{t("billing.receipt.ownerMissing")}</Notice> : null}
      {hasMore && nextCursor !== undefined ? (
        <div className="nave-form-actions">
          <BlobCtaButton type="button" size="sm" variant="secondary" disabled={loadingMore} loading={loadingMore} onClick={() => { void loadMore(); }}>
            {t(loadingMore ? "billing.invoices.loadingMore" : "billing.invoices.loadMore")}
          </BlobCtaButton>
        </div>
      ) : null}
      {loadMoreError === undefined ? null : (
        <Typography as="p" variant="caption" tone="muted" aria-live="polite">
          {t("billing.invoices.loadMoreError")}
        </Typography>
      )}
    </>
  );
};

type InvoiceFilter = "all" | "open" | "overdue" | "paid";

export const BillingPage = () => {
  const { runtime, activeOrganizationId } = useScenario();
  const { t, formatCurrency, formatDate } = useI18n();
  const key = `${runtime.scenario.id}:${activeOrganizationId.value}:billing`;
  const summary = useLoad<CustomerBillingSummary>(`${key}:summary`, () => runtime.experience.getBillingSummary());
  const invoices = useLoad<CustomerInvoiceList>(`${key}:invoices`, () => runtime.experience.listInvoices());

  if (summary.kind === "loading") return <PageSkeleton label={t("state.loading")} layout="list" />;
  if (summary.kind === "error") return <ErrorState code={summary.code} />;

  const next = summary.value.nextInvoice;
  const noAccount = summary.value.financialAccount === "NO_FINANCIAL_ACCOUNT";

  return (
    <div className="nave-page billing-experience-page">
      <PageHeader icon="lucide:receipt-text" title={t("billing.page.title")} description={t("billing.page.subtitle")} />

      {noAccount ? <EmptyState icon="lucide:wallet-minimal">{t("billing.noFinancialAccount")}</EmptyState> : next === undefined && summary.value.openCount === 0 && summary.value.overdueCount === 0 ? (
        <CardGlass variant="glass-md" elevation={0} className="billing-clear" role="status">
          <RowIcon name="lucide:circle-check" tone="success" />
          <span className="billing-clear-copy">
            <strong>{t("billing.summary.clear")}</strong>
            <span>{t("billing.summary.open")}: 0 · {t("billing.summary.overdue")}: 0</span>
          </span>
        </CardGlass>
      ) : (
        <section className="billing-overview" aria-labelledby="billing-summary-heading">
          <h2 id="billing-summary-heading" className="sr-only">{t("billing.summary.title")}</h2>
          <CardGlass variant="glass-md" elevation={0} className="billing-next">
            {next !== undefined ? <>
              <div className="nave-hero-card-top">
                <span className="nave-amount-label">{t("billing.nextInvoice")}</span>
                <GlassChip variant={summary.value.overdueCount > 0 ? "destructive" : "neutral"}>{summary.value.overdueCount > 0 ? t("billing.overdue") : t("billing.open")}</GlassChip>
              </div>
              <Typography as="p" variant="display-md" className="nave-amount-value">
                {formatCurrency(next.amount.value, next.amount.currency)}
              </Typography>
              <div className="nave-hero-card-top">
                <span className="nave-page-meta">
                  {next.serviceName ? <span className="nave-meta-item"><NaveIcon name="lucide:layers" /><PackageLabel text={next.serviceName} /></span> : null}
                  {next.dueAt !== undefined ? <span className="nave-meta-item"><NaveIcon name="lucide:calendar-clock" />{t("billing.due")}: {formatDate(next.dueAt)}</span> : null}
                </span>
                <BlobCtaAction href={`/billing/invoices/${next.invoiceId}`} size="sm" label={t("billing.invoice.view")} />
              </div>
            </> : <EmptyState icon="lucide:circle-check">{t("billing.summary.clear")}</EmptyState>}
          </CardGlass>
          <div className={summary.value.openCount > 0 ? "billing-stat" : "billing-stat billing-stat-quiet"}>
            <RowIcon name="lucide:hourglass" />
            <span className="nave-amount-label">{t("billing.summary.open")}</span>
            <strong>{summary.value.openCount}</strong>
          </div>
          <div className={summary.value.overdueCount > 0 ? "billing-stat billing-stat-alert" : "billing-stat billing-stat-quiet"}>
            <RowIcon name="lucide:triangle-alert" tone={summary.value.overdueCount > 0 ? "alert" : "neutral"} />
            <span className="nave-amount-label">{t("billing.summary.overdue")}</span>
            <strong>{summary.value.overdueCount}</strong>
          </div>
        </section>
      )}

      {!noAccount ? <Panel id="billing-invoices-heading" icon="lucide:files" title={t("billing.invoices.title")}>
        {invoices.kind === "loading" ? (
          <SectionSkeleton label={t("state.loading")} />
        ) : invoices.kind === "error" ? (
          <ErrorState inline code={invoices.code} />
        ) : (
          <BillingInvoiceListSection
            loadKey={`${key}:invoice-pages`}
            initial={invoices.value}
            loadNext={(cursor) => runtime.experience.listInvoices({ cursor })}
          />
        )}
      </Panel> : null}
    </div>
  );
};

export const BillingInvoiceDetailPage = () => {
  const { invoiceId = "" } = useParams();
  const { runtime, activeOrganizationId } = useScenario();
  const { t, formatCurrency, formatDate, formatMonth } = useI18n();
  const [revision, setRevision] = useState(0);
  const state = useLoad<CustomerInvoiceDetail>(
    `${runtime.scenario.id}:${activeOrganizationId.value}:invoice:${invoiceId}:${revision}`,
    () => runtime.experience.getInvoice(invoiceId),
  );

  useEffect(() => {
    // Returning from the hosted Cashier checkout re-reads owner state. No optimistic PAID state.
    const refresh = () => setRevision((current) => current + 1);
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [invoiceId]);

  if (state.kind === "loading") return <PageSkeleton label={t("state.loading")} layout="detail" />;
  if (state.kind === "error") return <ErrorState code={state.code} />;

  const invoice = state.value;
  const payable = invoice.status === "OPEN" || invoice.status === "OVERDUE";
  const title = invoice.serviceName || invoice.planName || t("billing.invoice.label");

  return (
    <div className="nave-page billing-experience-page">
      <PageHeader
        crumbs={[{ label: t("navigation.billing"), to: "/billing" }, { label: invoice.invoiceNumber ? t("billing.invoice.detailTitle", { id: invoice.invoiceNumber }) : title }]}
        icon="lucide:receipt-text"
        title={<PackageLabel text={title} />}
        status={{ label: t(invoiceStatusKey(invoice.status)), tone: statusVariant(invoice.status) === "destructive" ? "destructive" : invoice.status === "PAID" ? "success" : "neutral" }}
        description={invoice.description}
        meta={<>
          {invoice.invoiceNumber ? <span className="nave-meta-item"><NaveIcon name="lucide:hash" />{t("billing.invoice.detailTitle", { id: invoice.invoiceNumber })}</span> : null}
          {invoice.billingPhase ? <GlassChip variant="purple">{t(`billing.phase.${invoice.billingPhase}`)}</GlassChip> : null}
        </>}
        actions={<CtaLink href={`/api/invoices/${encodeURIComponent(invoice.invoiceId)}/download`} icon="lucide:download">{t("billing.invoice.download")}</CtaLink>}
      />

      <div className="nave-layout nave-layout-priority-aside">
        <div className="nave-layout-main">
          <Panel icon="lucide:calculator" title={t("billing.invoice.summary")}>
            <div className="nave-total">
              <div className="nave-amount">
                <span className="nave-amount-label">{t("billing.total")}</span>
                <Typography as="p" variant="display-md" className="nave-amount-value">
                  {formatCurrency(invoice.total.amount, invoice.total.currency)}
                </Typography>
              </div>
              {invoice.balance !== undefined ? (
                <div className="nave-amount">
                  <span className="nave-amount-label">{t("billing.balance")}</span>
                  <Typography as="p" variant="title-lg" className="nave-amount-value">{formatCurrency(invoice.balance.amount, invoice.balance.currency)}</Typography>
                </div>
              ) : null}
            </div>
            <Facts columns={3} items={([['subtotal', invoice.subtotal], ['additions', invoice.additions], ['amountPaid', invoice.amountPaid]] as const).map(([label, amount]) => amount === undefined ? null : { label: t(`billing.${label}`), value: formatCurrency(amount, invoice.total.currency) })} />
            {invoice.items.length > 0 ? <>
              <Typography as="h3" variant="title-sm">{t("billing.items.title")}</Typography>
              <ul className="nave-line-items">
                {invoice.items.map((item) => (
                  <li key={item.invoiceItemId} className="nave-line-item">
                    <div>
                      <Typography as="span" variant="body-md">{item.label}</Typography>
                      {item.periodStart !== undefined || item.periodEnd !== undefined ? (
                        <Typography as="span" variant="caption" tone="muted">
                          {item.periodStart !== undefined ? formatDate(item.periodStart) : t("billing.period.unspecified")}
                          {item.periodEnd !== undefined ? ` – ${formatDate(item.periodEnd)}` : ""}
                        </Typography>
                      ) : null}
                    </div>
                    <strong>{formatCurrency(item.amount.amount, item.amount.currency)}</strong>
                  </li>
                ))}
              </ul>
            </> : null}
          </Panel>

          {invoice.serviceName || invoice.planName || invoice.contractCode || invoice.includedItems?.length ? <Panel id="invoice-service-heading" icon="lucide:layers" title={t("billing.service")}>
            {invoice.serviceName ? <ServiceIdentity name={invoice.serviceName} logoUrl={invoice.serviceLogoUrl} planName={invoice.planName} /> : null}
            <Facts columns={2} items={[
              invoice.planName && !invoice.serviceName ? { label: t("billing.plan"), value: <PackageLabel text={invoice.planName}/>, icon: "lucide:gem" } : null,
              invoice.contractCode ? { label: t("billing.contract"), value: invoice.contractCode, icon: "lucide:file-signature" } : null,
            ]} />
            {invoice.includedItems?.length ? <ul className="nave-bullets">{invoice.includedItems.map((item, index) => <li key={index}>{item}</li>)}</ul> : null}
          </Panel> : null}

          <Panel id="invoice-history-heading" icon="lucide:history" title={t("billing.history.title")}>
            {invoice.paymentHistory?.length ? <ol className="nave-timeline">{invoice.paymentHistory.map((event, index) => <li key={index}><span className="nave-timeline-dot" aria-hidden="true"><NaveIcon name="lucide:banknote" /></span><div className="nave-timeline-body"><p className="nave-timeline-title nave-row-amount">{formatCurrency(event.amount, event.currency)}</p><span className="nave-timeline-time">{formatDate(event.paidAt)}{event.method ? ` · ${t(`billing.method.${event.method}`)}` : ""}</span></div></li>)}</ol> : <EmptyState icon="lucide:history">{t("billing.history.empty")}</EmptyState>}
            {invoice.status === "PAID" ? <Notice icon="lucide:file-check">{t("billing.receipt.ownerMissing")}</Notice> : null}
          </Panel>
        </div>

        <aside className="nave-layout-aside" aria-label={t("layout.context")}>
          <Panel id="invoice-payment-heading" icon="lucide:credit-card" title={t("billing.payment.title")}>
            <Facts items={[
              { label: t("billing.paymentStatus"), value: t(paymentStatusKey(invoice.paymentStatus)) },
              { label: t("billing.due"), value: formatDate(invoice.dueAt), icon: "lucide:calendar-clock" },
              invoice.paidAt !== undefined ? { label: t("billing.paidAt"), value: formatDate(invoice.paidAt), icon: "lucide:circle-check" } : null,
            ]} />
            {payable ? <Typography as="p" variant="body-sm" tone="muted">{t("billing.payment.explanation")}</Typography> : null}
            <div className="nave-actions-col">
              {payable && invoice.checkoutUrl ? <CtaLink href={invoice.checkoutUrl} external variant="primary" size="lg" icon="lucide:lock" trailingIcon="lucide:arrow-up-right">{t("billing.payment.cashier")}</CtaLink> : payable ? <Notice tone="warning">{t("billing.payment.unavailable")}</Notice> : null}
              {payable ? <BlobCtaButton type="button" size="sm" variant="secondary" onClick={() => setRevision((current) => current + 1)}>{t("billing.payment.refresh")}</BlobCtaButton> : null}
            </div>
          </Panel>
          <Panel id="invoice-period-heading" icon="lucide:calendar-range" title={t("billing.period.title")} tone="glass-sm">
            <Facts items={[
              invoice.competence ? { label: t("billing.competence"), value: formatMonth(invoice.competence) } : null,
              invoice.periodStart || invoice.periodEnd ? { label: t("billing.period.title"), value: `${invoice.periodStart ? formatDate(invoice.periodStart) : t("billing.period.unspecified")} – ${invoice.periodEnd ? formatDate(invoice.periodEnd) : t("billing.period.unspecified")}` } : null,
              invoice.issuedAt !== undefined ? { label: t("billing.issuedAt"), value: formatDate(invoice.issuedAt) } : null,
              { label: t("common.updatedAt"), value: formatDate(invoice.updatedAt) },
            ]} />
            {!invoice.competence && !invoice.periodStart && !invoice.periodEnd ? <Typography as="p" variant="body-sm" tone="muted">{t("billing.period.unspecified")}</Typography> : null}
          </Panel>
        </aside>
      </div>
    </div>
  );
};
